import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/material';

const CanvasElement = ({ element, isSelected, onClick, onDrag, onResize, scale }) => {
  const elementRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  
  const { position, size, properties, type, locked, visible } = element;
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging && !locked) {
        const deltaX = (e.clientX - dragStart.x) / scale;
        const deltaY = (e.clientY - dragStart.y) / scale;
        
        onDrag({
          x: Math.max(0, position.x + deltaX),
          y: Math.max(0, position.y + deltaY),
        });
        
        setDragStart({ x: e.clientX, y: e.clientY });
      }
      
      if (isResizing && !locked) {
        const deltaX = (e.clientX - resizeStart.x) / scale;
        const deltaY = (e.clientY - resizeStart.y) / scale;
        
        onResize({
          width: Math.max(20, resizeStart.width + deltaX),
          height: Math.max(20, resizeStart.height + deltaY),
        });
      }
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };
    
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart, resizeStart, position, locked, onDrag, onResize, scale]);
  
  const handleMouseDown = (e) => {
    if (locked) return;
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    onClick(e);
  };
  
  const handleResizeMouseDown = (e) => {
    if (locked) return;
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
    });
  };
  
  const renderContent = () => {
    switch (type) {
      case 'text':
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: properties.textAlign === 'center' ? 'center' : properties.textAlign === 'right' ? 'flex-end' : 'flex-start',
              justifyContent: properties.verticalAlign === 'center' ? 'center' : properties.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
              padding: '8px',
              fontSize: properties.fontSize || '16px',
              fontFamily: properties.fontFamily || 'Outfit',
              fontWeight: properties.fontWeight || 400,
              color: properties.color || '#000000',
              textAlign: properties.textAlign || 'left',
              wordWrap: 'break-word',
              overflow: 'hidden',
            }}
          >
            {properties.text || 'Text Element'}
          </Box>
        );
        
      case 'image':
        return (
          <img
            src={properties.src || 'https://via.placeholder.com/200'}
            alt={properties.alt || 'Image'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: properties.objectFit || 'cover',
            }}
          />
        );
        
      case 'video':
        return (
          <video
            src={properties.src}
            autoPlay={properties.autoPlay}
            loop={properties.loop}
            muted={properties.muted}
            style={{
              width: '100%',
              height: '100%',
              objectFit: properties.objectFit || 'cover',
            }}
          />
        );
        
      case 'menuItem':
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              padding: 2,
              backgroundColor: properties.backgroundColor || 'rgba(255, 255, 255, 0.9)',
            }}
          >
            {properties.image && (
              <img
                src={properties.image}
                alt={properties.name}
                style={{
                  width: '100%',
                  height: '60%',
                  objectFit: 'cover',
                  borderRadius: '8px',
                }}
              />
            )}
            <Box sx={{ marginTop: 1 }}>
              <Box sx={{ fontWeight: 600, fontSize: properties.nameFontSize || '18px' }}>
                {properties.name || 'Menu Item'}
              </Box>
              {properties.description && (
                <Box sx={{ fontSize: properties.descriptionFontSize || '14px', color: '#666', marginTop: 0.5 }}>
                  {properties.description}
                </Box>
              )}
              {properties.price && (
                <Box sx={{ fontSize: properties.priceFontSize || '16px', fontWeight: 600, color: properties.priceColor || '#C61F2F', marginTop: 0.5 }}>
                  ${properties.price}
                </Box>
              )}
            </Box>
          </Box>
        );
        
      default:
        return <Box>Unknown Element</Box>;
    }
  };
  
  if (!visible) return null;
  
  return (
    <Box
      ref={elementRef}
      onMouseDown={handleMouseDown}
      onClick={onClick}
      sx={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: element.zIndex || 1,
        cursor: locked ? 'default' : isDragging ? 'grabbing' : 'grab',
        border: isSelected ? '2px solid #C61F2F' : 'none',
        backgroundColor: properties.backgroundColor || 'transparent',
        borderRadius: `${properties.borderRadius || 0}px`,
        opacity: properties.opacity !== undefined ? properties.opacity : 1,
        pointerEvents: locked ? 'none' : 'auto',
        boxSizing: 'border-box',
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      {renderContent()}
      
      {/* Resize Handle */}
      {isSelected && !locked && (
        <Box
          onMouseDown={handleResizeMouseDown}
          sx={{
            position: 'absolute',
            right: -4,
            bottom: -4,
            width: 12,
            height: 12,
            backgroundColor: '#C61F2F',
            border: '2px solid white',
            borderRadius: '50%',
            cursor: 'nwse-resize',
            zIndex: 10,
            '&:hover': {
              backgroundColor: '#E42436',
            },
          }}
        />
      )}
    </Box>
  );
};

export default CanvasElement;



